import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightbulb, TrendingUp, Clock, ExternalLink, Edit, DollarSign, Link as LinkIcon, AlertTriangle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { StatusBadge } from '../components/StatusBadge';
import { PriceDeltaBar } from '../components/PriceDeltaBar';
import { EditProductModal } from '../components/EditProductModal';
import { MarketplacePricingModal } from '../components/MarketplacePricingModal';
import { CompetitorLinksModal } from '../components/CompetitorLinksModal';
interface ProductDetailProps {
  productId: number;
  onBack: () => void;
}
// Mock data generator based on ID
const getProductData = (id: number) => ({
  id,
  name: 'Smart TV Samsung 55" 4K UHD',
  category: 'Eletrônicos',
  price: 2899.0,
  minPrice: 2600.0,
  marketAvg: 2750.0,
  status: 'acima' as const,
  lastUpdate: 'Há 2 horas',
  sku: 'SAM-55-4K-2024',
  image: 'https://images.unsplash.com/photo-1593784991067-94594287122b?auto=format&fit=crop&q=80&w=200&h=200'
});
const historyData = [{
  date: '01/06',
  price: 2999,
  market: 2950
}, {
  date: '08/06',
  price: 2999,
  market: 2900
}, {
  date: '15/06',
  price: 2899,
  market: 2850
}, {
  date: '22/06',
  price: 2899,
  market: 2800
}, {
  date: '29/06',
  price: 2899,
  market: 2750
}, {
  date: '06/07',
  price: 2899,
  market: 2750
}];
const competitorsData = [{
  name: 'Amazon BR',
  price: 2699.0,
  diff: -6.9,
  updated: '15 min atrás'
}, {
  name: 'Magazine Luiza',
  price: 2749.0,
  diff: -5.2,
  updated: '1h atrás'
}, {
  name: 'Casas Bahia',
  price: 2799.0,
  diff: -3.4,
  updated: '2h atrás'
}, {
  name: 'Fast Shop',
  price: 2999.0,
  diff: 3.4,
  updated: '4h atrás'
}, {
  name: 'Kabum',
  price: 2650.0,
  diff: -8.6,
  updated: '30 min atrás'
}];
export function ProductDetail({
  productId,
  onBack
}: ProductDetailProps) {
  const product = getProductData(productId);
  const [activeModal, setActiveModal] = useState<'edit' | 'marketplace' | 'links' | null>(null);
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    duration: 0.3
  }} className="space-y-6" data-id="element-521">
      <div className="flex justify-between items-center" data-id="element-522">
        <button onClick={onBack} className="flex items-center text-slate-500 hover:text-navy-900 transition-colors font-medium" data-id="element-523">
          <ArrowLeft className="h-4 w-4 mr-2" data-id="element-524" />
          Voltar para Produtos
        </button>

        <div className="flex gap-2" data-id="element-525">
          <button onClick={() => setActiveModal('edit')} className="flex items-center px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-526">
            <Edit className="h-4 w-4 mr-2" data-id="element-527" />
            Editar
          </button>
          <button onClick={() => setActiveModal('marketplace')} className="flex items-center px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-528">
            <DollarSign className="h-4 w-4 mr-2" data-id="element-529" />
            Marketplaces
          </button>
          <button onClick={() => setActiveModal('links')} className="flex items-center px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-530">
            <LinkIcon className="h-4 w-4 mr-2" data-id="element-531" />
            Links
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-532">
        {/* Main Info Card */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-533">
          <div className="flex items-start justify-between" data-id="element-534">
            <div className="flex gap-4" data-id="element-535">
              <div className="h-24 w-24 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden" data-id="element-536">
                <img src={product.image} alt={product.name} className="h-full w-full object-cover" data-id="element-537" />
              </div>
              <div data-id="element-538">
                <div className="flex items-center gap-2 mb-1" data-id="element-539">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-540">
                    {product.category}
                  </span>
                  <StatusBadge status={product.status} data-id="element-541" />
                </div>
                <h1 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-542">
                  {product.name}
                </h1>
                <p className="text-sm text-slate-500" data-id="element-543">SKU: {product.sku}</p>
              </div>
            </div>
            <div className="text-right" data-id="element-544">
              <p className="text-sm text-slate-500 mb-1" data-id="element-545">Seu Preço</p>
              <p className="text-3xl font-bold text-navy-900" data-id="element-546">
                R${' '}
                {product.price.toLocaleString('pt-BR', {
                minimumFractionDigits: 2
              })}
              </p>
              <div className="flex items-center justify-end mt-1 gap-2" data-id="element-547">
                <PriceDeltaBar value={5.4} className="w-24" data-id="element-548" />
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-6" data-id="element-549">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-550">
              <p className="text-xs text-slate-500 mb-1" data-id="element-551">Preço Mínimo</p>
              <p className="font-bold text-navy-900" data-id="element-552">
                R${' '}
                {product.minPrice.toLocaleString('pt-BR', {
                minimumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-553">
              <p className="text-xs text-slate-500 mb-1" data-id="element-554">Margem Atual</p>
              <p className="font-bold text-emerald-600" data-id="element-555">18.5%</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-556">
              <p className="text-xs text-slate-500 mb-1" data-id="element-557">Buy Box</p>
              <p className="font-bold text-amber-600 flex items-center" data-id="element-558">
                <AlertTriangle className="h-3 w-3 mr-1" data-id="element-559" />
                Perdendo
              </p>
            </div>
          </div>

          <div className="mt-8" data-id="element-560">
            <h3 className="text-lg font-semibold text-navy-900 mb-4" data-id="element-561">
              Histórico de Preços (30 dias)
            </h3>
            <div className="h-[300px] w-full" data-id="element-562">
              <ResponsiveContainer width="100%" height="100%" data-id="element-563">
                <LineChart data={historyData} data-id="element-564">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-565" />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#64748b'
                }} dy={10} data-id="element-566" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748b'
                }} data-id="element-567" />
                  <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }} formatter={(value: number) => [`R$ ${value}`, '']} data-id="element-568" />
                  <Legend verticalAlign="top" height={36} data-id="element-569" />
                  <Line type="monotone" dataKey="price" name="Seu Preço" stroke="#1B2A4A" strokeWidth={3} dot={{
                  r: 4
                }} activeDot={{
                  r: 6
                }} data-id="element-570" />
                  <Line type="monotone" dataKey="market" name="Média de Mercado" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" dot={false} data-id="element-571" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="space-y-6" data-id="element-572">
          {/* Suggestion Box */}
          <div className="bg-[#E8EDF2] border-l-4 border-navy-900 rounded-r-xl p-5 shadow-sm" data-id="element-573">
            <div className="flex items-start gap-3" data-id="element-574">
              <Lightbulb className="h-6 w-6 text-navy-900 flex-shrink-0 mt-0.5" data-id="element-575" />
              <div data-id="element-576">
                <h4 className="font-bold text-navy-900 mb-1" data-id="element-577">
                  Sugestão de Preço
                </h4>
                <p className="text-sm text-navy-800 leading-relaxed" data-id="element-578">
                  Considere ajustar o preço para{' '}
                  <span className="font-bold" data-id="element-579">R$ 2.749,00</span> para ficar
                  competitivo com a média do mercado e ganhar destaque no Buy
                  Box.
                </p>
                <button className="mt-3 text-xs font-bold text-navy-900 uppercase tracking-wide border-b border-navy-900 pb-0.5 hover:opacity-75" data-id="element-580">
                  Aplicar Sugestão
                </button>
              </div>
            </div>
          </div>

          {/* Market Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-id="element-581">
            <h3 className="font-semibold text-navy-900 mb-4" data-id="element-582">
              Resumo do Mercado
            </h3>
            <div className="space-y-4" data-id="element-583">
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-584">
                <span className="text-sm text-slate-500" data-id="element-585">Média do Mercado</span>
                <span className="font-semibold text-navy-900" data-id="element-586">
                  R${' '}
                  {product.marketAvg.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2
                })}
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-587">
                <span className="text-sm text-slate-500" data-id="element-588">Menor Preço</span>
                <span className="font-semibold text-emerald-600" data-id="element-589">
                  R$ 2.650,00
                </span>
              </div>
              <div className="flex justify-between items-center pb-3 border-b border-slate-100" data-id="element-590">
                <span className="text-sm text-slate-500" data-id="element-591">Maior Preço</span>
                <span className="font-semibold text-red-600" data-id="element-592">R$ 2.999,00</span>
              </div>
              <div className="flex justify-between items-center" data-id="element-593">
                <span className="text-sm text-slate-500" data-id="element-594">Última Varredura</span>
                <span className="text-sm text-navy-900 flex items-center" data-id="element-595">
                  <Clock className="h-3 w-3 mr-1 text-slate-400" data-id="element-596" />
                  {product.lastUpdate}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Competitors Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-597">
        <div className="px-6 py-4 border-b border-slate-200" data-id="element-598">
          <h3 className="font-semibold text-navy-900" data-id="element-599">
            Comparativo de Concorrentes
          </h3>
        </div>
        <div className="overflow-x-auto" data-id="element-600">
          <table className="w-full text-left text-sm" data-id="element-601">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-602">
              <tr data-id="element-603">
                <th className="px-6 py-3" data-id="element-604">Concorrente</th>
                <th className="px-6 py-3" data-id="element-605">Preço</th>
                <th className="px-6 py-3" data-id="element-606">Diferença</th>
                <th className="px-6 py-3" data-id="element-607">Última Atualização</th>
                <th className="px-6 py-3" data-id="element-608">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-609">
              {competitorsData.map((comp, idx) => <tr key={idx} className="hover:bg-slate-50 transition-colors" data-id="element-610">
                  <td className="px-6 py-4 font-medium text-navy-900" data-id="element-611">
                    {comp.name}
                  </td>
                  <td className="px-6 py-4" data-id="element-612">
                    R${' '}
                    {comp.price.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2
                })}
                  </td>
                  <td className="px-6 py-4" data-id="element-613">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${comp.diff < 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`} data-id="element-614">
                      {comp.diff > 0 ? '+' : ''}
                      {comp.diff}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-500" data-id="element-615">{comp.updated}</td>
                  <td className="px-6 py-4" data-id="element-616">
                    <button className="text-navy-600 hover:text-navy-900" data-id="element-617">
                      <ExternalLink className="h-4 w-4" data-id="element-618" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      <EditProductModal isOpen={activeModal === 'edit'} onClose={() => setActiveModal(null)} product={product} onSave={data => console.log('Saved', data)} data-id="element-619" />

      <MarketplacePricingModal isOpen={activeModal === 'marketplace'} onClose={() => setActiveModal(null)} product={product} data-id="element-620" />

      <CompetitorLinksModal isOpen={activeModal === 'links'} onClose={() => setActiveModal(null)} product={product} data-id="element-621" />
    </motion.div>;
}